export type ProductUpdate = {
  id: string;
  date: string;
  area: string;
  title: string;
  paragraphs: string[];
  commits: string[];
  status?: 'preview';
};

// Dates are verified-by UTC deployment dates for the complete web entry, not commit dates.
// Native-only source history and individual receipts: design/docs/release-notes/2026-10-16-release-evidence.md.
export const PRODUCT_UPDATES: ProductUpdate[] = [
  {
    id: 'public-service-pages',
    date: '2026-10-13',
    area: 'Web',
    title: 'Read service information without signing in',
    paragraphs: [
      'Terms, privacy, and support information remain accessible without an account. Public pages use customer-facing language and explain where to get help.',
    ],
    commits: ['29cb9ca3', '161cc4d2', '003279d7'],
  },
  {
    id: 'private-cloud-trace-views',
    date: '2026-10-10',
    area: 'Web',
    title: 'Use your saved trace views across devices',
    paragraphs: [
      'Save a trace layout to your account and reopen it when signed in to the same account and workspace on another device. Saved definitions are private to you within that workspace; other members do not receive them.',
      'Your last selected view and Map View or Traces View preference stay local to this computer. Older browser-only saves require an explicit review and import into the displayed account and workspace; the original browser copies remain available.',
    ],
    commits: ['a04894e9bd44cced4aed0dc3a97e791bbfd9d813'],
  },
  {
    id: 'comparison-rail-gps-alignment',
    date: '2026-10-08',
    area: 'Web',
    title: 'Manage comparison laps and align traces by GPS',
    paragraphs: [
      'The comparison rail keeps the primary lap and every comparison lap visible in Map View and Traces View. Change or remove a comparison lap directly, and select a card to use that session in the full-session GPS overview.',
      'The three-dot Trace actions menu offers Align by Time and Align by GPS. Time keeps recorded elapsed time; GPS lines up equivalent positions along the primary lap’s driven path when all compared laps have usable GPS. The primary lap remains the replay and cursor reference.',
    ],
    commits: ['faf2f85d9195ce00089bd39f89e3e2c0e3eda2a4'],
  },
  {
    id: 'time-traces-without-gps',
    date: '2026-10-08',
    area: 'Web',
    title: 'Review recorded time traces without GPS',
    paragraphs: [
      'Sessions without usable GPS can still show their recorded telemetry in Traces View with time alignment. Review the channels that are present even when there is no map path.',
      'GPS alignment, map replay, and GPS-derived overlays still need recorded position data. Choosing a saved circuit does not recreate missing GPS.',
    ],
    commits: ['35ca8bc48f9d99dd193acc9382bfce4a501f7bd4'],
  },
  {
    id: 'email-preferences',
    date: '2026-10-07',
    area: 'Web',
    title: 'Choose whether to receive Paddiq updates',
    paragraphs: [
      'Manage optional Paddiq email preferences from your account or the unsubscribe link in an email. Opting out of outreach keeps essential account, security, and billing messages available.',
    ],
    commits: ['cfc6c447', '53573b03', '76fc7bf5'],
  },
  {
    id: 'resource-guides-refresh',
    date: '2026-10-02',
    area: 'Web',
    title: 'Find guidance for your next session',
    paragraphs: [
      'The Resources library brings account access, imports, lap comparison, circuit setup, troubleshooting, and billing guidance together. The refreshed homepage explains the workflow and links directly to the product and guides.',
    ],
    commits: ['1e9edff2'],
  },
  {
    id: 'session-folders',
    date: '2026-09-28',
    area: 'Web',
    title: 'Browse sessions by date and driver',
    paragraphs: [
      'Open a date, expand a driver, and choose a session time. Check additional sessions to compare them, or select a session time to make it primary without clearing the other runs.',
      'Use Compare another lap to add more laps from the primary session. The folder list labels primary and comparison sessions.',
    ],
    commits: ['bcb66e1685c0f490e34ad4a2d0442773ca8e3f6c'],
  },
  {
    id: 'remember-review-layout',
    date: '2026-09-28',
    area: 'Web',
    title: 'Return to your last review layout',
    paragraphs: [
      'Session Review remembers Map View or Traces View and your last saved trace view on this computer. A deleted or unavailable saved view falls back to the default without blocking review.',
    ],
    commits: ['bcb66e1685c0f490e34ad4a2d0442773ca8e3f6c'],
  },
  {
    id: 'saved-session-assignments',
    date: '2026-09-28',
    area: 'Web',
    title: 'Keep saved driver and kart assignments in review',
    paragraphs: [
      'Session lists and review use the driver and kart saved for the run. Clearing an assignment stays cleared instead of bringing back a value from the logger.',
    ],
    commits: ['3d3d0b8c8007a1e2e8fcf184cbdacee5eeda154c'],
  },
  {
    id: 'session-timeline',
    date: '2026-09-28',
    area: 'Web',
    title: 'See the session timeline alongside your traces',
    paragraphs: [
      'A session timeline shows lap boundaries and GPS position across the run. Select a lap from the timeline and resize the overview to give it more or less room.',
    ],
    commits: ['de46d8fff58618c8547e60bffc98e31a01c274ef'],
  },
  {
    id: 'map-display-controls',
    date: '2026-09-28',
    area: 'Web',
    title: 'Adjust the map while reviewing traces',
    paragraphs: [
      'Resize the map panel beside your charts. Map display controls include GPS line only and track annotations, so you can choose how much detail to show. Your map display preferences are remembered on this computer.',
    ],
    commits: ['de46d8fff58618c8547e60bffc98e31a01c274ef'],
  },
  {
    id: 'trace-size-and-zoom',
    date: '2026-09-28',
    area: 'Web',
    title: 'Resize and zoom telemetry charts',
    paragraphs: [
      'Drag to resize graphs and zoom into a section with the mouse wheel or zoom controls. Reset the zoom to return to the full lap.',
      'Graph display settings persist, so you can return to the arrangement you use for review.',
    ],
    commits: [
      '80d60ec6cb20df4d36b1ce61f3271d1e31681018',
      '76a7b95c96f7e3d861e46bad5332b121f16acdb6',
      'ad75a1587f06e955f82da57bc08d0bf430ddc4e8',
      '4b076e0b43bb4314aa20ab39f20973af147dad2d',
    ],
  },
  {
    id: 'provider-buttons',
    date: '2026-09-28',
    area: 'Web',
    title: 'Consistent Google and Apple sign-in controls',
    paragraphs: [
      'Google and Apple sign-in buttons now use consistent widths and alignment on the account screen.',
    ],
    commits: [
      '91bb7aede007c981b01f54eede371d9f969e4a9d',
      '932b279de24939332994258d41f8d00e78f7bdf1',
    ],
  },
  {
    id: 'faster-session-catalogs',
    date: '2026-09-28',
    area: 'Web',
    title: 'Open sessions with less waiting',
    paragraphs: [
      'Session lists reuse cached results while refreshing. Circuit and driver lookups do less repeated work, and larger circuit catalogs load in pages.',
      'Background lookups no longer need to finish before the first available sessions can appear.',
    ],
    commits: [
      'cbd6bc572d3639022a2c6dccf88ca619d4b30d84',
      'd79285f24b4c28ee599196bc2ed8f5489ae7e79f',
      'bcb66e1685c0f490e34ad4a2d0442773ca8e3f6c',
    ],
  },
  {
    id: 'driver-garage-access',
    date: '2026-09-28',
    area: 'Web',
    title: 'Drivers can update their assigned kart setup',
    paragraphs: [
      'Drivers can edit setup for their assigned kart when their workspace allows it. Team members can read the feature access they need without being required to manage billing.',
      'Garage controls also respect secondary team roles. Setup access does not grant permission to change the wider fleet.',
    ],
    commits: [
      'd9a8d49525c4a73e6bad7f0ce85500ddf73497fa',
      'e77d2211113facb1c2b5f81d8b0e056e27fdc207',
      'd50567ad5992149fb432b069e29d5b158a99332e',
    ],
  },
  {
    id: 'accurate-comparison-duration',
    date: '2026-09-28',
    area: 'Web',
    title: 'Compare each lap over its actual duration',
    paragraphs: [
      'Telemetry charts keep each lap’s real duration and show where it finishes. Shorter laps no longer appear stretched to match a longer comparison.',
      'Comparison sessions are matched using their assigned circuit. Loading fixes prevent an earlier request from replacing the session you just selected.',
    ],
    commits: [
      '21b4b345bd3b330ad56287a3386bd65ab2b90a58',
      '315b780b943bfb5751afe715bac99de1e02c18b5',
      '7cc0d36a68ec0bd005d0c3b8e45d4e4dc848322c',
    ],
  },
  {
    id: 'complimentary-partner-access',
    date: '2026-09-28',
    area: 'Web',
    title: 'See when partner access covers your sessions',
    paragraphs: [
      'Accounts with active partner coverage show Account access and a complimentary-access message. Session usage is $0 while that coverage is active.',
      'Existing billing controls remain available for accounts that already have a payment method or subscription.',
    ],
    commits: [
      '5cc5419b2deb44b64b186d66f254e8e925b18ba6',
      'd50567ad5992149fb432b069e29d5b158a99332e',
    ],
  },
  {
    id: 'paddiq-link-preview',
    date: '2026-09-17',
    area: 'Paddiq Link',
    title: 'Paddiq Link desktop workflow preview',
    paragraphs: [
      'We’re building a desktop companion for managing MyChron loggers and transferring sessions to Paddiq. Development includes guided connection setup, driver and kart assignments, circuit selection, and session history.',
      'Logger records and shared assignments follow the signed-in workspace. Transfers can pause and resume, and downloaded files are retained through interrupted uploads. Google and Apple sign-in are included in the work.',
      'This September preview records development work, not a desktop release date. See Paddiq Link Beta for current capabilities and verified downloads; use the web importer or iPhone app when a beta download is unavailable.',
    ],
    commits: [
      '6cef66d95edd39d16dc5cc29c9a3e213e7cd8f51',
      '29c04b60a8683f5995d597e93042625073e85a39',
      'b11e78a819f2a928ee8a5dd8a567a4be4b555d18',
      '96df0d62f8cc2a7712a7992e149e4f45a91521e4',
      '610edbd55d6ac75db86437b4afce519e928a33db',
      'd8f0ecb91bdf1d89a4974d9dc2041c99ca9c7f0b',
    ],
    status: 'preview',
  },
  {
    id: 'guided-session-workflows',
    date: '2026-09-10',
    area: 'Web',
    title: 'Move from an upload to a useful session review with less guesswork',
    paragraphs: [
      'The MyChron importer now accepts multiple supported files, checks them before upload, shows real progress, and lets you confirm the driver, eligible vehicle, and recommended circuit. The circuit step overlays the recorded line with the saved geometry before Paddiq slices laps and reports the best lap.',
      'Sessions added a searchable session picker, a sortable session table, and separate Map View and Traces View workspaces. Trace views can be configured, saved, reordered, combined, and replayed over a selected section.',
      'Garage now opens vehicles in one mechanic-focused drawer with autosaving quick setup, photos, custom fields, reusable templates, details, and history. Footage, Timings, and Recordings also use clearer language for pairing camera video with Paddiq timing and MyChron data.',
    ],
    commits: [
      '71582d75f7df102c2f61eb78165e218c1662bb1c',
      '446a832dffb2109c6e3b72945b8375d2fa03b470',
    ],
  },
  {
    id: 'lap-setup-snapshots',
    date: '2026-09-10',
    area: 'Web',
    title: 'Save setup details for a specific lap',
    paragraphs: [
      'Lap-specific setup snapshots preserve the equipment settings associated with that part of a session. Updating the kart’s current setup does not replace a saved historical snapshot.',
      'Garage autosave fixes keep related field edits together and preserve role-based editing controls.',
    ],
    commits: [
      'd76f60b563cc09c0a771a415e63950df3f5fe043',
      '20556aa617d81e2b51b86ee3d5f3c05a72e0663e',
    ],
  },
  {
    id: 'session-storage-recovery',
    date: '2026-09-10',
    area: 'Web',
    title: 'More reliable session loading and recovery',
    paragraphs: [
      'Session data, original recordings, and saved assignments are handled separately so a derived-data repair does not replace the original source.',
      'Updates improve cached-session refresh, upload recovery, and session links that open the requested run.',
    ],
    commits: [
      'e31145a1a5ec3802be974925537fe121239d0791',
      '3a091a963168dc6e6d6c0564ddb51698b7cf94ff',
      '36633b51add50174abff1fb0ebc14f6dd177a230',
      'f084ee5bf73928c741281be1fc8e5f0b6bfff1c2',
    ],
  },
  {
    id: 'sessions-replay-speed',
    date: '2026-09-10',
    area: 'Web',
    title: 'Choose your replay speed in Sessions',
    paragraphs: [
      'The compact replay controls in Sessions now include a Replay speed menu beside the play button.',
      'Choose 0.5x, 1x, 2x, or 4x to slow down a moment or move through a lap faster.',
    ],
    commits: ['71582d75f7df102c2f61eb78165e218c1662bb1c'],
  },
  {
    id: 'consistent-session-driver-labels',
    date: '2026-09-07',
    area: 'Web',
    title: 'Keep session driver labels in sync',
    paragraphs: [
      'When you assign, reassign, or clear a driver, Paddiq now carries the saved choice from Assignments into session lists, filters, comparisons, and session pickers.',
      'A run you intentionally leave without a driver is labeled Driver unassigned instead of falling back to a name recorded by the logger.',
    ],
    commits: ['d00ca775a7c874aa76de25933c8719376a22d897'],
  },
  {
    id: 'gopro-replay-integrity',
    date: '2026-09-03',
    area: 'Web',
    title: 'Recover interrupted footage processing',
    paragraphs: [
      'Queued footage processing can resume after an interruption. A failed retry preserves a replay that is already available, and cloud-only footage keeps its correct status.',
      'Replay billing and upload retries have additional safeguards against duplicate usage charges.',
    ],
    commits: [
      'ed6b74811ed7064662445d7955a9b2cd0a1e77a2',
      '67b62deaa2ecc6d26ee8d7e8c62a3a6b92624a7e',
      '6e6d7a5dca5bfcec50e140bd5efd20d833ac4310',
      'ff19e3a37e2bafc52ffbf47f48a4d8b4f4d9e59c',
      '6a4b3173c84ce713a0b43883126fea923cee6030',
    ],
  },
  {
    id: 'original-mychron-files',
    date: '2026-09-03',
    area: 'Sessions',
    title: 'Preserve original MyChron files',
    paragraphs: [
      'Original logger files have stronger ownership and verification checks. Upload retries preserve a completed session instead of resetting its state.',
      'Session processing checks for meaningful telemetry and keeps the original file separate from derived analysis.',
    ],
    commits: [
      'e5bb70dd41e2a3b966d367ece755de388b8ead3e',
      '9e98c1b91d041caf95cab77cc5e26127a18f800c',
      '52bbfac67afd606f5fb3a15102f2301167c7b872',
      '28f774188cd2791eefa3e451c731f11293dd9385',
    ],
  },
  {
    id: 'kiosk-account-privacy',
    date: '2026-09-03',
    area: 'Kiosk',
    title: 'Close shared-screen account access after inactivity',
    paragraphs: [
      'Shared kiosk account sessions return to the station after inactivity. Sign-out and interrupted close requests keep the previous person’s session hidden while access is cleared.',
    ],
    commits: [
      '92a282d9fd5e6b3da8e00400558b64c0e95e8f37',
      '3479ecd20b56978000630ac355e45b28cd88cea4',
      'd3a61f5412a69cb43af0e101d3d75a50668303e5',
    ],
  },
  {
    id: 'exact-session-scans',
    date: '2026-08-29',
    area: 'Web',
    title: 'Open the session linked to a QR code',
    paragraphs: [
      'Authenticated session links open the requested run rather than a previously selected session. Kiosk setup separates station pairing from session QR codes and guides first-time scans.',
      'Concurrent scans and repeated handoffs have stronger checks so a later request cannot reveal an earlier visitor’s session.',
    ],
    commits: [
      'd058f832017ac3fd21291d26a5a924b8330adf4f',
      '825f0e4d4acb88f7cfcf3aaa83192ab07722e827',
      '3e1e6250215ba19af9aeee9cc43c049452039a6c',
      'c3ecc2d91610c615c8a60867d3a992eb5bfc817d',
    ],
  },
  {
    id: 'complete-sessions-history',
    date: '2026-08-27',
    area: 'Web',
    title: 'Load your complete Sessions history',
    paragraphs: [
      'The SESSION & LAPS chooser can now load sessions beyond the first 100 sessions, so older sessions remain available for review and lap comparisons.',
      'The current web drawer continues loading older sessions automatically while it is open. If loading fails, use the retry action shown in the drawer.',
    ],
    commits: ['a91186fb6e9b5b4d43b257a6c718543d9e5c645e'],
  },
  {
    id: 'safer-circuit-lap-timing',
    date: '2026-08-27',
    area: 'Web',
    title: 'Keep lap timing matched to the current circuit',
    paragraphs: [
      'When a session is assigned to another circuit, or its saved circuit layout changes, Paddiq no longer reuses fixed laps created for the previous configuration.',
      'The current circuit must have its laps regenerated and saved before that timing is reused in Sessions or Telemetry. This keeps lap boundaries and comparisons matched to the circuit shown on the map.',
    ],
    commits: ['d05164c79f5dd972f105b4bd9b37e48beea5c1f2'],
  },
  {
    id: 'fastest-recorded-lap-reference',
    date: '2026-08-27',
    area: 'Web and web kiosk',
    title: "Compare with the driver's fastest recorded lap",
    paragraphs: [
      "Session and telemetry maps can now show the selected driver's fastest valid lap for the same saved circuit configuration. The dashed reference is labeled Fastest recorded lap.",
      'Paddiq keeps the reference unavailable when the driver, circuit, access, or valid-lap evidence does not match, rather than substituting a lap from another driver or layout.',
    ],
    commits: [
      '959e969cdb6a3fda97db4155a9c1388a6a70add8',
      '3d0b4f715744a3757e05657702c2d3e488e0f728',
    ],
  },
  {
    id: 'billing-recovery',
    date: '2026-08-27',
    area: 'Accounts',
    title: 'More reliable payment and usage recovery',
    paragraphs: [
      'Payment recovery checks the full subscription history rather than a short initial list. Delayed usage reporting can resume without repeatedly charging the same uploaded session.',
      'Free-upload counts and monthly estimates are reconciled with the responsible billing account.',
    ],
    commits: [
      '23ced4a7ab794ad2a92d69510eb2e404634f4f6f',
      '296c9c00013521be84ef026516db90a968c9cc64',
      '7309e13490d2c46073002b2e42496211c606a596',
      '168db2c58a0458d3686a2062094e9405aaa5cd52',
    ],
  },
  {
    id: 'gps-clock-recovery',
    date: '2026-08-27',
    area: 'Web',
    title: 'Recover circuit crossings when the GPS clock resets',
    paragraphs: [
      'Circuit lap setup can recover crossings from older recordings with GPS clock resets. A valid saved start/finish line is still required.',
    ],
    commits: ['ece2f7abe38bece9fba55baf267ee190f27d4471'],
  },
  {
    id: 'complete-driver-rosters',
    date: '2026-08-27',
    area: 'Web',
    title: 'Complete driver rosters',
    paragraphs: [
      'Larger teams can now find every driver in Team Management and the Drivers directory, including drivers beyond the first 100 records. Search, profile access, editing, linking, and role-aware controls work across the complete roster.',
    ],
    commits: [
      '827d1c465bf99dd55caf0f4f8f3fb3a532155ade',
      '99a7c9511aff9734f647923da71f59234dbd0d1f',
      '33643c3dbfdeffc240fccf1723a9b79f6f65df3f',
    ],
  },
  {
    id: 'private-circuit-details',
    date: '2026-08-24',
    area: 'Web',
    title: 'Private circuit details stay protected',
    paragraphs: [
      'Circuit layouts that your team is still preparing can now only be opened by members of that team. Other teams cannot use a direct link to view the layout.',
      'Layouts that have been shared in Paddiq remain available wherever shared circuits are used.',
    ],
    commits: ['b2a95187fedbed05b2da418e0b37d2c92ffecca4'],
  },
  {
    id: 'partner-upload-access',
    date: '2026-08-23',
    area: 'Partner teams',
    title: 'Partner uploads stay available',
    paragraphs: [
      'Teams with active partner access can now keep uploading sessions after the first five free uploads without adding a payment method.',
      'Team owners can apply a code under Partner code in Usage billing. When Paddiq shows Partner access applied, retry the paused upload.',
    ],
    commits: ['b7f315e92711fce11a4ccf37a00946f9ceeca6b7'],
  },
  {
    id: 'safer-kiosk-closing',
    date: '2026-08-22',
    area: 'Web kiosk',
    title: 'Safer kiosk session closing',
    paragraphs: [
      'Selecting DONE, or letting the display close after inactivity, now hides session telemetry immediately. If the kiosk temporarily loses its connection, the session stays hidden while Paddiq retries the close, including after the browser restarts.',
    ],
    commits: [
      'a9b23df9c3ae466f4255cc4af6c056eac4775e19',
      '45522a2c7025dce38722be094dddce0f7428f8ae',
    ],
  },
  {
    id: 'complete-kiosk-history',
    date: '2026-08-22',
    area: 'Web kiosk',
    title: 'Complete kiosk session history',
    paragraphs: [
      'The kiosk SESSION & LAPS chooser can now load the complete session history available to that station instead of stopping after the first 100 sessions.',
      'Paddiq shows how many sessions are loaded and lets you load more until the list is complete. If the list changes while it is loading, the kiosk asks you to reload instead of showing an incomplete set of choices.',
    ],
    commits: ['8678829aa9b4c6c85ea280f4b623628342420801'],
  },
  {
    id: 'complete-gopro-history',
    date: '2026-08-22',
    area: 'Web',
    title: 'Complete GoPro upload history',
    paragraphs: [
      'You can now load older GoPro uploads in Uploads and when adding footage to a session, instead of seeing only the 10 most recent uploads.',
      'Clear loading, completion, and retry messages show whether more footage is available. If a later group fails to load, you can retry loading the history.',
    ],
    commits: [
      'cf32d7a74a94e73551eee8ff2024dbb31935d4ce',
      '768631427d1c2487096861d445ee2324f02d5b7e',
    ],
  },
  {
    id: 'reliable-partner-codes',
    date: '2026-08-21',
    area: 'Web',
    title: 'More reliable partner codes',
    paragraphs: [
      'Partner-code redemption now handles simultaneous requests safely. One-time codes stay tied to the first team that redeems them, while approved multi-use codes remain available to eligible teams.',
      'Retrying a code from the same team produces a consistent result without accidentally consuming another redemption.',
    ],
    commits: [
      '0a90c72a0339512198b6d85168d78e753632748e',
      '6207ccd20a52fe833a409336c2a17d3708e5d865',
    ],
  },
  {
    id: 'searchable-circuit-layouts',
    date: '2026-08-18',
    area: 'Web',
    title: 'Find circuit layouts faster',
    paragraphs: [
      'When a session needs a circuit, you can now search the complete saved circuit list instead of scrolling through a short set of nearby matches.',
      'Layouts are grouped by venue, with direction and distance included to help you choose the exact configuration for the session.',
    ],
    commits: ['818cb9300b0258f2e59446e9576f4e6bc5796de4'],
  },
  {
    id: 'account-and-file-recovery',
    date: '2026-08-18',
    area: 'Accounts',
    title: 'Safer account setup and file cleanup',
    paragraphs: [
      'Interrupted account setup has better recovery, and account identifiers and setup links have stronger checks against conflicting or repeated requests.',
      'File deletion and replaced profile images can finish cleanup after an interruption without restoring a deleted record.',
    ],
    commits: [
      'd78dbc0c972a27493d3b5a742190706647896a26',
      '65b42505e90df0e044991791d3368482339ffb87',
      '4e04c12eba9e041c2b36b4fb7244754b755f9e8a',
      '51c7b9fe0f48da07f7ecd47d2aaefd64ad4cdc11',
      '1b170208e3aadbaf2647dd60e9e95e91b68b6e27',
    ],
  },
  {
    id: 'cleaner-session-maps',
    date: '2026-08-13',
    area: 'Web',
    title: 'Cleaner session maps',
    paragraphs: [
      'Session maps now ignore brief GPS errors that place one or a few points far away from the circuit and then immediately return. The circuit outline stays centered on the driving path instead of shrinking around a long out-and-back line.',
      'Paddiq continues to preserve real movement to another location. The cleanup is limited to isolated invalid fixes and short bursts when the surrounding points remain close together.',
    ],
    commits: ['16cdb5951ef30bc724c5d4609362e5dbc81c27ae'],
  },
  {
    id: 'session-usage-pricing',
    date: '2026-08-12',
    area: 'Accounts',
    title: 'Session-based billing and free uploads',
    paragraphs: [
      'Session billing introduced five lifetime-free uploads, followed by an active-period charge that includes five billable sessions. Inactive periods have no session usage charge.',
      'See the session billing guide for current prices, partner coverage, and who pays for an upload. This replaced earlier plan and trial arrangements.',
    ],
    commits: [
      '957b984d97d7ceb9545136abc2d9e7169ef2da15',
      '66687679712f5722dcc10210814792231b748b69',
      '83ae024f59dbbfac271bffb28a4bfb18fdfc62a4',
    ],
  },
  {
    id: 'guided-circuit-setup',
    date: '2026-08-05',
    area: 'Web',
    title: 'Find a circuit and build laps from its start/finish line',
    paragraphs: [
      'Guided circuit setup helps match a run to a saved layout and generate circuit laps. Circuit search groups configurations by venue and helps distinguish layout and direction.',
    ],
    commits: [
      'fbaf5eea03b72a33d31ce66e918320d25efd7f8d',
      '612f5f6b3688de96b8286b530a5b6d0375fe244a',
      '6ac993fb6913ab93ea5a702ff699a807bd96393f',
      '7de3aba633b5acdc4354d41f92aecb6eb3f504e4',
    ],
  },
  {
    id: 'replay-and-overlays',
    date: '2026-08-01',
    area: 'Web',
    title: 'Replay laps with traces and corner analysis',
    paragraphs: [
      'Replay controls, lap comparisons, turn-speed tables, and map overlays were added and refined on the web.',
      'Braking and coasting overlays estimate changes from recorded speed. They do not measure brake-pedal or throttle input.',
    ],
    commits: [
      '6cf02910fa362182f0e097ec6b2338314a4f2a00',
      'd71edc06e0d30947e65a516610a4b82e69ad970e',
      '40152c1c0f39b3672b51ffc71313b3c488390016',
    ],
  },
  {
    id: 'garage-and-circuit-catalog',
    date: '2026-07-24',
    area: 'Web',
    title: 'Manage vehicles and find circuit configurations',
    paragraphs: [
      'The circuit catalog added richer layout information and faster session matching.',
    ],
    commits: [
      'f0fc64a97bec80a249cdba6624fa2a8c45eb6a15',
      'a7c994e502ae377bf9d9b3d066b9451627616305',
      '93e727edc1b87985feb855e90d2c16206af45a9b',
    ],
  },
  {
    id: 'driver-and-family-accounts',
    date: '2026-07-19',
    area: 'Accounts',
    title: 'Set up driver and family access',
    paragraphs: [
      'Drivers can create their own workspace, and family managers can manage linked drivers and vehicles. Team-managed drivers and username sign-in support club workflows.',
      'Account appearance follows the device theme by default.',
    ],
    commits: [
      'd966eb48e026366710ebfa7de1687588496da0f9',
      'aaf28d58c398a67c49f03bfa7940ad6f0796105d',
      'afd59bf65c33d2e14ebbbdec29c15583ec952338',
      'dba69fdde08bb259d635e326d2c191a08216e95d',
    ],
  },
  {
    id: 'gpx-imports',
    date: '2026-07-16',
    area: 'Web',
    title: 'Import GPS recordings alongside MyChron data',
    paragraphs: [
      'GPX import provides GPS-based session data. Other supporting files have a separate upload path and do not become native MyChron sensor channels.',
    ],
    commits: [
      '34ff73091dfa798ca83d4e1e20ae898049aed0cd',
      'fe98eb67043cdaf9027a4bbf2b88bed9223d6d46',
    ],
  },
  {
    id: 'account-sign-in',
    date: '2026-07-10',
    area: 'Web',
    title: 'More sign-in options and account controls',
    paragraphs: [
      'Google and Apple sign-in were added alongside password access. Account controls include password recovery and account deletion.',
    ],
    commits: [
      'e1f92eabe556523a81f8bae9cef51f914a3f390d',
      '271f99019f74f1fb09498bcce7feb8d62d8bf43b',
      'e49087b464233bc3ef412c378ebfa86400ee6ce8',
    ],
  },
  {
    id: 'footage-and-feedback',
    date: '2026-07-07',
    area: 'Web',
    title: 'Review footage with sessions and report a problem',
    paragraphs: [
      'Session footage can be aligned with lap timing, with indicators for footage overlapping a lap. Video uses its original aspect ratio.',
      'In-app feedback gives teams a way to report an issue from the product.',
    ],
    commits: [
      'c84845d4772f7a8e419def677bf93c59fbaaf479',
      'a0da28a0f09d17d278d2768005f514a26ae6e906',
      '812deb371016452f9de87be4898e6f9756cf8002',
      '1a639845c4b565ce6cef6bacb4ed77cdad1aab80',
    ],
  },
  {
    id: 'leaderboard-foundation',
    date: '2026-07-02',
    area: 'Web',
    title: 'Review your fastest sessions by circuit',
    paragraphs: [
      'Leaderboard groups recorded results by circuit, shows time gaps, and provides the vehicle setup available for each run.',
    ],
    commits: ['10bfebe2103a0238584750bf6bc39fa28c0bf954'],
  },
  {
    id: 'team-workspace-foundation',
    date: '2026-07-02',
    area: 'Web',
    title: 'Organize team members and family access',
    paragraphs: [
      'Team workspaces added multiple roles per member, parent-managed fleets, family join requests, and team codes. Support and partner-code access were also introduced.',
    ],
    commits: [
      '8145e31861731d3f26152f00911fa8fd74d634b5',
      '22aaeec883bb8d5c37927dde038aab016dc6a12f',
      '1b52887371d49d4e148d725554a7768eca0ddacc',
      '667a17593064edf04e427059fe64fce648609f74',
      '107bbce76e6668464f101edef4bc2ae0dfbaa0a8',
    ],
  },
  {
    id: 'session-review-foundation',
    date: '2026-06-29',
    area: 'Web',
    title: 'Upload MyChron sessions and compare laps',
    paragraphs: ['The initial session workspace introduced map replay and lap comparisons.'],
    commits: [
      '92c3d04e6fbfecdf0d55c01fb3cf126e0828b31b',
      '399d0dff022bfbcee3c2a5ba9f70be274926d22d',
      'f3bb13cf82b198ac6e56cd1e29de03bcaf585976',
      '75773072e33d8f6bd05ac6254b36e3460aceb1d7',
      '3af116acc363700ef2c26af3e4ed08d044e6f65c',
    ],
  },
];
